import { AssetNameOverview } from "@/components/Comment/AssetNameOverview";
import { CommentPane } from "@/components/Comment/CommentPane";
import {
  useCurrentUploadQuery,
  useUploadUpdateMutation,
} from "@/hooks/uploads";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { APPROVED_STATES } from "./constants";
import { Loading } from "@/components/Loading/Loading";
import { CommentLinkPane } from "@/components/Comment/CommentLinkPane";
import { NavigationButtons } from "@/components/NavigationButtons/NavigationButtons";
import UploadTitle from "@/components/UploadTitle/UploadTitle";
import GenericModal from "@/components/GenericModal/GenericModal";
import DeleteUpload from "@/components/DeleteUpload/DeleteUpload";
import { AppRouteError, toRouteError } from "@/errors/routeError";
import { useCurrentUploadId } from "@/hooks/uploads";

const COMMENTS_FORBIDDEN_MESSAGE = {
  title: "No access to comments",
  body: "You don't have permission to view these comments. Please ensure you have access to the related request.",
};

// Manual override of the COMMENTS_FORBIDDEN message for this page since the currentUpload fetch resolves before the backend can return the proper COMMENTS_FORBIDDEN error.
export function toCommentsRouteError(
  error: unknown,
  uploadId?: number,
): AppRouteError {
  const routeError = toRouteError(error, { scope: "comments", uploadId });
  return routeError.code === "NOT_OWNER"
    ? new AppRouteError({
        ...routeError,
        code: "COMMENTS_FORBIDDEN",
        ...COMMENTS_FORBIDDEN_MESSAGE,
      })
    : routeError;
}

const CommentsPage = () => {
  const navigate = useNavigate();
  const [isCommentModalVisible, setIsCommentModalVisible] = useState(false);
  const uploadUpdateMutation = useUploadUpdateMutation();
  const uploadId = useCurrentUploadId();
  const currentUploadQuery = useCurrentUploadQuery();
  const childRef = useRef<{ onCommentSubmit: () => Promise<void> }>();

  if (currentUploadQuery.isError) {
    throw toCommentsRouteError(currentUploadQuery.error, uploadId);
  }

  if (!currentUploadQuery.isSuccess) {
    return <Loading text="Loading comments page" />;
  }

  const upload = currentUploadQuery.data!;

  const isApproved = APPROVED_STATES.includes(upload.state);

  const handleShareWithAdmin = async () => {
    await childRef.current?.onCommentSubmit();
    if (upload.state === "ADMIN_FEEDBACK") {
      await uploadUpdateMutation.mutateAsync({
        id: uploadId,
        updateUploadDto: {
          state: "USER_RESPONSE",
        },
      });
      navigate("/overview");
    }
  };

  return (
    <div className="mx-[20px] flex flex-col">
      <UploadTitle upload={upload} />
      <section className="flex space-x-space250">
        <div className="w-[600px] space-y-space100">
          <CommentPane
            scrollHeight="450px"
            ref={childRef}
            upload={upload}
            overrideOnSubmit={handleShareWithAdmin}
          />
        </div>
        <div className="vertical-divider" />
        <div className="flex w-[600px] flex-col">
          <CommentLinkPane
            uploadType={upload.type}
            uploadId={uploadId}
            isApproved={isApproved}
            onOpenCommentModal={() => setIsCommentModalVisible(true)}
          />

          <div className="mt-space50 flex flex-col items-start justify-start gap-y-space100">
            <NavigationButtons
              prevButtonTitle="Back to overview"
              nextButtonTitle="Share with administrator"
              disabledNext={true} // Disabled in DAM-2807, reenable in DAM-2919?
              onPrev={() => {
                navigate("/overview");
              }}
              onContinue={handleShareWithAdmin}
            />
            {upload.state === "SUBMITTED" && (
              <DeleteUpload
                upload={upload}
                buttonText="Cancel request"
                navigate={() => navigate("/overview")}
              />
            )}
          </div>
        </div>
      </section>
      <GenericModal
        modalVisible={isCommentModalVisible}
        hideModal={() => setIsCommentModalVisible(false)}
        ariaLabel="Asset name changes"
        size="large"
      >
        <AssetNameOverview assets={upload.assets} />
      </GenericModal>
    </div>
  );
};

export default CommentsPage;
